/**
 * Converts plain text URLs in HTML content to clickable anchor tags for email tracking.
 * 
 * This is essential for SES click tracking to work - SES only wraps <a> tags, not plain URLs.
 * 
 * @param html - The HTML content that may contain plain text URLs
 * @returns HTML with all plain text URLs converted to anchor tags
 * 
 * @example
 * Input: "<p>Check out https://example.com for details</p>"
 * Output: "<p>Check out <a href="https://example.com">https://example.com</a> for details</p>"
 */
export function convertPlainUrlsToLinks(html: string): string {
  if (!html) return html;

  // URL regex pattern that matches http(s):// URLs not already in href attributes
  // This pattern looks for URLs that are NOT preceded by href=" or href='
  const urlPattern = /(?<!href=["'])(https?:\/\/[^\s<>"]+?)(?=[\s<]|$)/gi;

  // Replace plain URLs with anchor tags
  const result = html.replace(urlPattern, (url) => {
    // Double-check this URL isn't already inside an <a> tag
    // by looking at the surrounding context
    return `<a href="${url}">${url}</a>`;
  });

  return result;
}

/**
 * Validates that URLs in HTML are wrapped in anchor tags for tracking.
 * Returns true if all URLs are properly wrapped.
 */
export function validateUrlsAreLinked(html: string): boolean {
  if (!html) return true;

  // Find all plain URLs that are NOT inside anchor tags
  const plainUrlPattern = /(?<!href=["'])(https?:\/\/[^\s<>"]+?)(?=[\s<]|$)/gi;
  const plainUrls = html.match(plainUrlPattern);

  return plainUrls === null || plainUrls.length === 0;
}
